// CanvasCropOverlay — IN-CANVAS rectangle crop.
//
// Unlike ImageCropOverlay (which blows the image up fullscreen on a dark
// backdrop), this draws a resizable crop frame DIRECTLY on top of the selected
// image box, right where it sits on the canvas. The user moves/resizes the
// frame with 8 handles + a draggable body, then confirms (✓). The ✕ / ✓ live
// in the ToolsPanel "Kırp" popover, not on the canvas: this overlay exposes
// its confirm via `confirmRef` and reports its busy state via
// `onLoadingChange`. The selected
// region is mapped from screen px → the image's NATURAL pixels and cropped via
// the shared cropRegion() helper, uploaded, and applied back onto the box.
//
// Geometry:
//   - box container DOM id : `box-{boxId}`   (the clipped viewport of the box)
//   - image element DOM id : `{boxId}-image` (actual <img>, may overflow box)
// The crop frame is stored relative to the box's top-left and re-glued to the
// box every animation frame so it tracks scroll/zoom while open.

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import i18n from "i18next";
import { cropRegion } from "./ImageCropOverlay";

const PURPLE = "#a963f5";
const MIN = 20; // min crop size (px)
const H = 12; // handle hit size (px)

// Handles: [dir, cursor, position-style]. dir letters drive resize math.
const HANDLES = [
  ["nw", "nwse-resize", { left: -H / 2, top: -H / 2 }],
  ["n", "ns-resize", { left: "50%", top: -H / 2, marginLeft: -H / 2 }],
  ["ne", "nesw-resize", { right: -H / 2, top: -H / 2 }],
  ["e", "ew-resize", { right: -H / 2, top: "50%", marginTop: -H / 2 }],
  ["se", "nwse-resize", { right: -H / 2, bottom: -H / 2 }],
  ["s", "ns-resize", { left: "50%", bottom: -H / 2, marginLeft: -H / 2 }],
  ["sw", "nesw-resize", { left: -H / 2, bottom: -H / 2 }],
  ["w", "ew-resize", { left: -H / 2, top: "50%", marginTop: -H / 2 }],
];

function clampCrop(c, bw, bh) {
  let w = Math.max(MIN, Math.min(c.w, bw));
  let h = Math.max(MIN, Math.min(c.h, bh));
  let x = Math.max(0, Math.min(c.x, bw - w));
  let y = Math.max(0, Math.min(c.y, bh - h));
  return { x, y, w, h };
}

export default function CanvasCropOverlay({
  boxId,
  imageUrl,
  aspect,
  onCropped,
  onClose,
  confirmRef,
  onLoadingChange,
}) {
  const [boxRect, setBoxRect] = useState(null); // live box viewport rect (screen px)
  const [crop, setCrop] = useState(null); // { x, y, w, h } relative to boxRect
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const boxElRef = useRef(null);
  const imgElRef = useRef(null);
  const dragRef = useRef(null); // { mode, startX, startY, orig }
  const aspectRef = useRef(null); // mirrors `aspect` for the stable onMove handler
  const boundsRef = useRef(null); // max croppable area (image rect within box), rel. to box

  useEffect(() => {
    aspectRef.current = aspect ?? null;
  }, [aspect]);

  // Kırp açıkken seçili görselin over-box aksiyon barını (görsel değiştir /
  // ekrana sığdır / Aidea Wizard / daha fazla) gizle — kırpma çerçevesiyle
  // çakışmasın. Sınıf yalnızca overlay mount'luyken durur, unmount'ta kalkar.
  useEffect(() => {
    document.body.classList.add("crop-overlay-open");
    return () => document.body.classList.remove("crop-overlay-open");
  }, []);

  // React to a ratio picked in the tools panel: fit the largest rect of this
  // ratio inside the FULL croppable area (image bounds), centred on the current
  // frame. Reading box size off the ref (not boxRect state) keeps this off the
  // per-frame rAF churn so it won't fight manual dragging.
  useEffect(() => {
    if (!aspect) return;
    const bounds = boundsRef.current;
    const boxEl = boxElRef.current;
    if (!bounds || !boxEl) return;
    const br = boxEl.getBoundingClientRect();
    setCrop((c) => {
      const base = c || bounds;
      const cx = base.x + base.w / 2;
      const cy = base.y + base.h / 2;
      let w = bounds.w;
      let h = w / aspect;
      if (h > bounds.h) {
        h = bounds.h;
        w = h * aspect;
      }
      return clampCrop({ x: cx - w / 2, y: cy - h / 2, w, h }, br.width, br.height);
    });
  }, [aspect]);

  // Locate the box + image DOM elements and seed an inset crop frame.
  useLayoutEffect(() => {
    const boxEl = document.getElementById(`box-${boxId}`);
    const imgEl =
      document.getElementById(`${boxId}-image`) ||
      (boxEl && boxEl.querySelector("img.basicImageClass")) ||
      (boxEl && boxEl.querySelector("img"));
    boxElRef.current = boxEl;
    imgElRef.current = imgEl;
    if (!boxEl) {
      setError(i18n.t("CropFailed"));
      return;
    }
    const br = boxEl.getBoundingClientRect();
    setBoxRect(br);
    // Seed the crop frame over the whole image (real crop UX): the user grabs
    // the edge / corner handles and pulls INWARD — not draw-a-box-from-scratch,
    // which read as "cutting". Fall back to the full box if the image rect
    // isn't measurable yet.
    let bounds = { x: 0, y: 0, w: br.width, h: br.height };
    if (imgEl) {
      const ir = imgEl.getBoundingClientRect();
      const x = Math.max(0, ir.left - br.left);
      const y = Math.max(0, ir.top - br.top);
      const w = Math.min(br.width, ir.right - br.left) - x;
      const h = Math.min(br.height, ir.bottom - br.top) - y;
      if (w > 1 && h > 1) bounds = { x, y, w, h };
    }
    boundsRef.current = bounds;
    setCrop(clampCrop(bounds, br.width, br.height));
  }, [boxId]);

  // Keep the frame glued to the box while the canvas scrolls / zooms.
  useEffect(() => {
    let raf;
    const sync = () => {
      const boxEl = boxElRef.current;
      if (boxEl) setBoxRect(boxEl.getBoundingClientRect());
      raf = requestAnimationFrame(sync);
    };
    raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Esc cancels (unless mid-crop).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const onMove = useCallback((e) => {
    const d = dragRef.current;
    const boxEl = boxElRef.current;
    if (!d || !boxEl) return;
    const br = boxEl.getBoundingClientRect();
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const o = d.orig;
    const m = d.mode;
    let c = { ...o };
    if (m === "move") {
      c.x = o.x + dx;
      c.y = o.y + dy;
    } else {
      if (m.includes("e")) c.w = o.w + dx;
      if (m.includes("s")) c.h = o.h + dy;
      if (m.includes("w")) {
        c.x = o.x + dx;
        c.w = o.w - dx;
      }
      if (m.includes("n")) {
        c.y = o.y + dy;
        c.h = o.h - dy;
      }
      // Keep the anchored edge fixed when dragging past the minimum.
      if (c.w < MIN) {
        if (m.includes("w")) c.x = o.x + o.w - MIN;
        c.w = MIN;
      }
      if (c.h < MIN) {
        if (m.includes("n")) c.y = o.y + o.h - MIN;
        c.h = MIN;
      }
      // Lock to the selected aspect ratio (if any), keeping the opposite edge
      // anchored. Side handles grow symmetrically on the free axis.
      const ar = aspectRef.current;
      if (ar) {
        const corner = (m.includes("n") || m.includes("s")) && (m.includes("e") || m.includes("w"));
        const vertOnly = m === "n" || m === "s";
        if (vertOnly) {
          c.w = c.h * ar;
          c.x = o.x + (o.w - c.w) / 2;
        } else {
          c.h = c.w / ar;
          if (corner) {
            c.y = m.includes("n") ? o.y + o.h - c.h : o.y;
          } else {
            c.y = o.y + (o.h - c.h) / 2;
          }
        }
      }
    }
    setCrop(clampCrop(c, br.width, br.height));
  }, []);

  const onUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  }, [onMove]);

  const startDrag = useCallback(
    (mode) => (e) => {
      if (loading || !crop) return;
      e.preventDefault();
      e.stopPropagation();
      setError(null);
      dragRef.current = { mode, startX: e.clientX, startY: e.clientY, orig: { ...crop } };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [loading, crop, onMove, onUp]
  );

  useEffect(() => () => onUp(), [onUp]);

  const confirm = useCallback(async () => {
    if (loading || !crop) return;
    const imgEl = imgElRef.current;
    const boxEl = boxElRef.current;
    if (!imgEl || !boxEl) {
      setError(i18n.t("CropFailed"));
      return;
    }
    const br = boxEl.getBoundingClientRect();
    const ir = imgEl.getBoundingClientRect();
    const sx = br.left + crop.x;
    const sy = br.top + crop.y;
    // Intersect the selection with the rendered image, then map to natural px.
    const left = Math.max(sx, ir.left);
    const top = Math.max(sy, ir.top);
    const right = Math.min(sx + crop.w, ir.right);
    const bottom = Math.min(sy + crop.h, ir.bottom);
    if (right <= left || bottom <= top || !imgEl.naturalWidth) {
      setError(i18n.t("CropFailed"));
      return;
    }
    const scaleX = imgEl.naturalWidth / ir.width;
    const scaleY = imgEl.naturalHeight / ir.height;
    const rectNatural = {
      left: (left - ir.left) * scaleX,
      top: (top - ir.top) * scaleY,
      width: (right - left) * scaleX,
      height: (bottom - top) * scaleY,
    };

    setLoading(true);
    setError(null);
    try {
      const blob = await cropRegion(imageUrl, rectNatural);
      // Where the crop sits relative to the box's positioning parent, as
      // percentages. The caller re-lays-out the box to exactly this rect so the
      // crop keeps BOTH its size and its on-screen place (no shrink-to-corner,
      // no fit-to-screen). Percentages are zoom-independent (they're ratios of
      // the same parent), so canvas zoom cancels out.
      const parentEl = boxEl.offsetParent || boxEl.parentElement;
      const pr = parentEl ? parentEl.getBoundingClientRect() : br;
      const layout =
        pr.width && pr.height
          ? {
              xPct: ((left - pr.left) / pr.width) * 100,
              yPct: ((top - pr.top) / pr.height) * 100,
              wPct: ((right - left) / pr.width) * 100,
              hPct: ((bottom - top) / pr.height) * 100,
            }
          : null;
      await onCropped?.(blob, layout);
    } catch (err) {
      console.error("[CanvasCropOverlay] Crop failed:", err);
      setError((err && err.userMessage) || i18n.t("CropFailed"));
      setLoading(false);
    }
  }, [loading, crop, imageUrl, onCropped]);

  // Hand the current confirm to the popover's ✓ button.
  useEffect(() => {
    if (!confirmRef) return undefined;
    confirmRef.current = confirm;
    return () => {
      if (confirmRef.current === confirm) confirmRef.current = null;
    };
  }, [confirmRef, confirm]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  if (!boxRect) return null;

  const hasCrop = crop && crop.w > 1 && crop.h > 1;
  const cs = hasCrop
    ? { left: boxRect.left + crop.x, top: boxRect.top + crop.y, w: crop.w, h: crop.h }
    : null;
  const dim = {
    position: "fixed",
    background: "rgba(0,0,0,0.5)",
    pointerEvents: "none",
    zIndex: 100000,
  };

  return (
    <>
      {/* Inert capture layer over the box: swallows clicks on the darkened area
          so they neither start a fresh "cut" rectangle nor fall through to the
          canvas (which would deselect the box). Sits below the frame. */}
      {!loading && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            left: boxRect.left,
            top: boxRect.top,
            width: boxRect.width,
            height: boxRect.height,
            cursor: "default",
            zIndex: 100000,
          }}
        />
      )}

      {hasCrop && (
        <>
          {/* Darken the box area outside the crop rectangle (4 bands). */}
          <div
            style={{
              ...dim,
              left: boxRect.left,
              top: boxRect.top,
              width: boxRect.width,
              height: crop.y,
            }}
          />
          <div
            style={{
              ...dim,
              left: boxRect.left,
              top: cs.top + cs.h,
              width: boxRect.width,
              height: Math.max(0, boxRect.height - crop.y - crop.h),
            }}
          />
          <div style={{ ...dim, left: boxRect.left, top: cs.top, width: crop.x, height: crop.h }} />
          <div
            style={{
              ...dim,
              left: cs.left + cs.w,
              top: cs.top,
              width: Math.max(0, boxRect.width - crop.x - crop.w),
              height: crop.h,
            }}
          />

          {/* Crop frame */}
          <div
            onMouseDown={startDrag("move")}
            style={{
              position: "fixed",
              left: cs.left,
              top: cs.top,
              width: cs.w,
              height: cs.h,
              border: `2px dashed ${PURPLE}`,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.6)",
              cursor: loading ? "wait" : "move",
              zIndex: 100001,
            }}
          >
            {!loading &&
              HANDLES.map(([dir, cursor, pos]) => (
                <div
                  key={dir}
                  onMouseDown={startDrag(dir)}
                  style={{
                    position: "absolute",
                    width: H,
                    height: H,
                    background: "#fff",
                    border: `2px solid ${PURPLE}`,
                    borderRadius: 2,
                    cursor,
                    ...pos,
                  }}
                />
              ))}

            {error && (
              <div
                style={{
                  position: "absolute",
                  bottom: -28,
                  left: 0,
                  color: "#fca5a5",
                  background: "rgba(0,0,0,0.7)",
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {error}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
